import React, { useState, useRef } from "react"
import styles from "./TagInput.module.css"
import Label from "./Label"
import RequiredNotice from "./RequiredNotice"
import t from "frontend/utils/translations"
import { Icon } from "frontend/components/icons"
import { ERRORS } from "./constants"

const CHARACTER_LIMIT = 30

const TagInput = (props) => {
  const { id, label, onChange, value, required, footerText, tagSuggestions, limit, error, className } = props

  const tags = value
  const inputRef = useRef(null)

  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(0)

  const tagMap = new Map()
  tags.forEach((tag) => tagMap.set(tag, true))
  const hasReachedTagLimit = tags.length >= limit

  const [inputValue, setInputValue] = useState("")
  const [hasReachedCharacterLimit, setHasReachedCharacterLimit] = useState(false)

  const autocompleteOptions = inputValue.length
    ? tagSuggestions
        .filter((suggestion) => {
          return suggestion.includes(inputValue) && !tagMap.get(suggestion)
        })
        .slice(0, 5)
    : []

  const handleAddTag = (newTag) => {
    onChange([...tags, newTag])
  }

  const handleRemoveTag = (tagToRemove) => {
    onChange(tags.filter((tag) => tag !== tagToRemove))
  }

  const takeSuggestion = (suggestion) => {
    setInputValue("")
    handleAddTag(suggestion)
    inputRef.current.focus()
  }

  const submitTag = () => {
    setHasReachedCharacterLimit(false)

    if (!inputValue) {
      return
    }

    const text = inputValue.trim()
    const existingTag = tags.find((tag) => tag === text)

    if (existingTag) {
      return
    }
    handleAddTag(text)
    setInputValue("")
  }

  const handleKeyDown = (e) => {
    if (autocompleteOptions.length > 0) {
      if (e.key === "Enter") {
        e.stopPropagation()
        e.preventDefault()
        takeSuggestion(autocompleteOptions[focusedSuggestionIndex])
      }

      if (e.key === "ArrowDown" && focusedSuggestionIndex < autocompleteOptions.length - 1) {
        e.stopPropagation()
        e.preventDefault()

        setFocusedSuggestionIndex(focusedSuggestionIndex + 1)
      }

      if (e.key === "ArrowUp" && focusedSuggestionIndex > 0) {
        e.stopPropagation()
        e.preventDefault()

        setFocusedSuggestionIndex(focusedSuggestionIndex - 1)
      }
    } else {
      if (e.key === "Enter") {
        e.stopPropagation()
        e.preventDefault()
        submitTag()
      }
    }
  }

  const handleBlur = () => {
    submitTag()
  }

  const handleInputChange = (e) => {
    setFocusedSuggestionIndex(0)

    if (e.target.value) {
      if (e.target.value.length >= CHARACTER_LIMIT) {
        setHasReachedCharacterLimit(true)
      } else if (hasReachedCharacterLimit) {
        setHasReachedCharacterLimit(false)
      }
      const string = e.target.value.substring(0, CHARACTER_LIMIT)
      setInputValue(string)
    } else {
      setInputValue("")
    }
  }

  return (
    <div className={`${styles.container} ${className}`} data-testid="taginput-component">
      <Label id={id} required={required} hasError={Boolean(error)}>
        {label}
      </Label>
      <div className={`${styles.inputContainer} ${error ? styles.error : ""}`} data-testid="tag-input-container">
        <div data-testid="tag-container" className={styles.tagContainer}>
          {tags.map((tag) => (
            <span key={tag} data-testid="tag" className={styles.tag}>
              {tag}
              <button
                aria-label="close tag"
                className={styles.closeTag}
                data-testid={`close-${tag.toLowerCase()}`}
                onClick={() => handleRemoveTag(tag)}
              >
                <Icon name="cancel" />
              </button>
            </span>
          ))}
          {tags.length < limit && (
            <input
              ref={inputRef}
              id={id}
              data-testid="input"
              size="1"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className={styles.input}
              onBlur={handleBlur}
            />
          )}
        </div>
      </div>
      {autocompleteOptions.length > 0 && (
        <div className={styles.autocompleteContainer}>
          <div data-testid="autocomplete" className={styles.autocomplete}>
            {autocompleteOptions.map((suggestion, i) => (
              <a
                key={suggestion}
                className={`${styles.suggestion} ${i === focusedSuggestionIndex ? styles.focusedSuggestion : ""}`}
                data-testid="suggestion"
                onMouseDown={() => takeSuggestion(suggestion)}
              >
                {suggestion}
              </a>
            ))}
          </div>
        </div>
      )}
      <RequiredNotice required={required} hasError={error === ERRORS.required} />
      {footerText && !hasReachedTagLimit && (
        <span className={styles.footerText} data-testid="footer-text">
          {footerText}
        </span>
      )}

      {hasReachedTagLimit && (
        <span className={styles.errorText} data-testid="error">
          <Icon name="danger" /> {t("form.tags.tag_limit", { limit })}
        </span>
      )}

      {hasReachedCharacterLimit && !hasReachedTagLimit && (
        <span className={styles.errorText} data-testid="error">
          <Icon name="danger" /> {t("form.tags.character_limit", { limit: CHARACTER_LIMIT })}
        </span>
      )}
    </div>
  )
}

TagInput.defaultProps = {
  onChange: () => {},
  error: "",
  value: [],
  tagSuggestions: [],
  limit: 20,
  className: "",
}

export default TagInput
